import { createSeededRandom } from '../../core/random';
import { PROTOCOL_VERSION } from '../../core/protocols';
import { clampDifficulty } from '../../core/staircase';
import type { SessionMode, TrialPlan, TrialResult } from '../../core/types';
import { drawCenteredText } from '../canvas';
import type {
  ModuleFeedback,
  ResponseOption,
  RuntimeTrainingModule,
  TrainingFrameContext,
} from '../runtime';
import { estimateReversalThreshold } from './reversal-threshold';
import { estimateFixedFormThreshold } from './fixed-threshold';
import { adaptDifficultyToPerformance } from '../adaptive-difficulty';

export type GapDirection = 'up' | 'right' | 'down' | 'left';
export type MovingTargetStartSide = GapDirection;
export const MOVING_TARGET_HALF_PATH_DEGREES = 10.5;
export const MOVING_TARGET_GUIDE_HALF_PATH_DEGREES = 11;
export const MOVING_TARGET_HALF_HEIGHT_DEGREES = 4.2;
export const MOVING_TARGET_DURATION_MS = 1_400;
const MOVING_TARGET_PATH_STEP_MS = 200;

export interface MovingTargetPathNode {
  elapsedMs: number;
  xDegrees: number;
  yDegrees: number;
}

export interface MovingTargetStimulus {
  gapDirection: GapDirection;
  startSide: MovingTargetStartSide;
  sizeArcminutes: number;
  speedDegreesPerSecond: number;
  displayDurationMs: number;
  pathProfile: 'seeded-random-walk';
  pathNodes: MovingTargetPathNode[];
}

const RESPONSE_OPTIONS: readonly ResponseOption[] = [
  { value: 'up', keys: ['ArrowUp'], label: '上', visual: 'arrow-up' },
  { value: 'right', keys: ['ArrowRight'], label: '右', visual: 'arrow-right' },
  { value: 'down', keys: ['ArrowDown'], label: '下', visual: 'arrow-down' },
  { value: 'left', keys: ['ArrowLeft'], label: '左', visual: 'arrow-left' },
];

export function difficultyToArcminutes(difficulty: number): number {
  return 22 - (clampDifficulty(difficulty, 1, 5) - 1) * 3;
}

export function difficultyToMovingTargetSpeed(difficulty: number): number {
  return Number((24 + (clampDifficulty(difficulty, 1, 5) - 1) * 4).toFixed(2));
}

export function difficultyToMovingTargetDurationMs(difficulty: number): number {
  return Math.round(MOVING_TARGET_DURATION_MS - (clampDifficulty(difficulty, 1, 5) - 1) * 280);
}

function createMovingTargetStart(
  startSide: MovingTargetStartSide,
  random: ReturnType<typeof createSeededRandom>,
): { xDegrees: number; yDegrees: number; headingRadians: number } {
  switch (startSide) {
    case 'left':
      return {
        xDegrees: -MOVING_TARGET_HALF_PATH_DEGREES,
        yDegrees: random.range(-MOVING_TARGET_HALF_HEIGHT_DEGREES * 0.72, MOVING_TARGET_HALF_HEIGHT_DEGREES * 0.72),
        headingRadians: random.range(-Math.PI / 3, Math.PI / 3),
      };
    case 'right':
      return {
        xDegrees: MOVING_TARGET_HALF_PATH_DEGREES,
        yDegrees: random.range(-MOVING_TARGET_HALF_HEIGHT_DEGREES * 0.72, MOVING_TARGET_HALF_HEIGHT_DEGREES * 0.72),
        headingRadians: Math.PI + random.range(-Math.PI / 3, Math.PI / 3),
      };
    case 'up':
      return {
        xDegrees: random.range(-MOVING_TARGET_HALF_PATH_DEGREES * 0.72, MOVING_TARGET_HALF_PATH_DEGREES * 0.72),
        yDegrees: -MOVING_TARGET_HALF_HEIGHT_DEGREES,
        headingRadians: Math.PI / 2 + random.range(-Math.PI / 3, Math.PI / 3),
      };
    case 'down':
      return {
        xDegrees: random.range(-MOVING_TARGET_HALF_PATH_DEGREES * 0.72, MOVING_TARGET_HALF_PATH_DEGREES * 0.72),
        yDegrees: MOVING_TARGET_HALF_HEIGHT_DEGREES,
        headingRadians: -Math.PI / 2 + random.range(-Math.PI / 3, Math.PI / 3),
      };
  }
}

function pointInsideMovingTargetBounds(xDegrees: number, yDegrees: number): boolean {
  return Math.abs(xDegrees) <= MOVING_TARGET_HALF_PATH_DEGREES
    && Math.abs(yDegrees) <= MOVING_TARGET_HALF_HEIGHT_DEGREES;
}

function createRandomPathNodes(
  startSide: MovingTargetStartSide,
  speedDegreesPerSecond: number,
  displayDurationMs: number,
  random: ReturnType<typeof createSeededRandom>,
): MovingTargetPathNode[] {
  const start = createMovingTargetStart(startSide, random);
  const nodes: MovingTargetPathNode[] = [{
    elapsedMs: 0,
    xDegrees: start.xDegrees,
    yDegrees: start.yDegrees,
  }];
  let headingRadians = start.headingRadians;
  let current = nodes[0] as MovingTargetPathNode;

  while (current.elapsedMs < displayDurationMs) {
    const elapsedMs = Math.min(displayDurationMs, current.elapsedMs + MOVING_TARGET_PATH_STEP_MS);
    const stepDurationMs = elapsedMs - current.elapsedMs;
    const distanceDegrees = speedDegreesPerSecond * (stepDurationMs / 1_000);
    let accepted: MovingTargetPathNode | undefined;

    for (let attempt = 0; attempt < 24; attempt += 1) {
      const candidateHeading = attempt === 0 && elapsedMs === MOVING_TARGET_PATH_STEP_MS
        ? headingRadians
        : random.range(-Math.PI, Math.PI);
      const xDegrees = current.xDegrees + Math.cos(candidateHeading) * distanceDegrees;
      const yDegrees = current.yDegrees + Math.sin(candidateHeading) * distanceDegrees;
      if (!pointInsideMovingTargetBounds(xDegrees, yDegrees)) continue;
      headingRadians = candidateHeading;
      accepted = { elapsedMs, xDegrees, yDegrees };
      break;
    }

    if (!accepted) {
      const targetX = random.range(-MOVING_TARGET_HALF_PATH_DEGREES * 0.55, MOVING_TARGET_HALF_PATH_DEGREES * 0.55);
      const targetY = random.range(-MOVING_TARGET_HALF_HEIGHT_DEGREES * 0.55, MOVING_TARGET_HALF_HEIGHT_DEGREES * 0.55);
      headingRadians = Math.atan2(targetY - current.yDegrees, targetX - current.xDegrees);
      accepted = {
        elapsedMs,
        xDegrees: current.xDegrees + Math.cos(headingRadians) * distanceDegrees,
        yDegrees: current.yDegrees + Math.sin(headingRadians) * distanceDegrees,
      };
    }

    nodes.push(accepted);
    current = accepted;
  }

  return nodes;
}

export function createMovingTargetStimulus(
  seed: number,
  difficulty: number,
): MovingTargetStimulus {
  const random = createSeededRandom(seed);
  const startSide = random.pick<MovingTargetStartSide>(['left', 'right', 'up', 'down']);
  const speedDegreesPerSecond = difficultyToMovingTargetSpeed(difficulty);
  const displayDurationMs = difficultyToMovingTargetDurationMs(difficulty);
  return {
    gapDirection: random.pick<GapDirection>(['up', 'right', 'down', 'left']),
    startSide,
    sizeArcminutes: difficultyToArcminutes(difficulty),
    speedDegreesPerSecond,
    displayDurationMs,
    pathProfile: 'seeded-random-walk',
    pathNodes: createRandomPathNodes(startSide, speedDegreesPerSecond, displayDurationMs, random),
  };
}

function gapAngle(direction: GapDirection): number {
  switch (direction) {
    case 'right':
      return 0;
    case 'down':
      return Math.PI / 2;
    case 'left':
      return Math.PI;
    case 'up':
      return -Math.PI / 2;
  }
}

function drawLandoltC(
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  diameter: number,
  direction: GapDirection,
): void {
  const radius = Math.max(2.5, diameter / 2);
  const stroke = Math.max(1.5, radius * 0.42);
  const angle = gapAngle(direction);
  const halfGap = 0.38;
  context.save();
  context.strokeStyle = '#f0f2ee';
  context.lineWidth = stroke;
  context.lineCap = 'butt';
  context.beginPath();
  context.arc(x, y, radius, angle + halfGap, angle + Math.PI * 2 - halfGap);
  context.stroke();
  context.restore();
}

export function movingTargetHorizontalDegrees(
  stimulus: MovingTargetStimulus,
  elapsedMs: number,
): number {
  return movingTargetPositionDegrees(stimulus, elapsedMs).xDegrees;
}

export function movingTargetPositionDegrees(
  stimulus: MovingTargetStimulus,
  elapsedMs: number,
): { xDegrees: number; yDegrees: number } {
  const boundedElapsed = Math.min(stimulus.displayDurationMs, Math.max(0, elapsedMs));
  const nextIndex = stimulus.pathNodes.findIndex((node) => node.elapsedMs >= boundedElapsed);
  if (nextIndex <= 0) {
    const first = stimulus.pathNodes[0] as MovingTargetPathNode;
    return { xDegrees: first.xDegrees, yDegrees: first.yDegrees };
  }
  const previous = stimulus.pathNodes[nextIndex - 1] as MovingTargetPathNode;
  const next = stimulus.pathNodes[nextIndex] as MovingTargetPathNode;
  const progress = (boundedElapsed - previous.elapsedMs) / (next.elapsedMs - previous.elapsedMs);
  return {
    xDegrees: previous.xDegrees + (next.xDegrees - previous.xDegrees) * progress,
    yDegrees: previous.yDegrees + (next.yDegrees - previous.yDegrees) * progress,
  };
}

function renderMovingTarget(
  context: CanvasRenderingContext2D,
  frame: TrainingFrameContext,
  trial: TrialPlan<MovingTargetStimulus>,
): void {
  context.save();
  context.setTransform(frame.devicePixelRatio, 0, 0, frame.devicePixelRatio, 0, 0);
  context.fillStyle = '#171a19';
  context.fillRect(0, 0, frame.width, frame.height);
  const stimulus = trial.stimulus;
  const centerX = frame.width / 2;
  const centerY = frame.height / 2;
  if (frame.elapsed < trial.timings.stimulusMs) {
    const position = movingTargetPositionDegrees(stimulus, frame.elapsed);
    const x = centerX + position.xDegrees * frame.pixelsPerDegree;
    const y = centerY + position.yDegrees * frame.pixelsPerDegree;
    const diameter = (stimulus.sizeArcminutes / 60) * frame.pixelsPerDegree;
    drawLandoltC(context, x, y, diameter, stimulus.gapDirection);
  } else {
    drawCenteredText(context, 'Cの切れ目はどちら向き？', centerX, centerY - 10, {
      size: 24,
      color: '#f0f2ee',
      weight: 650,
    });
    drawCenteredText(context, '下の方向ボタンで回答', centerX, centerY + 34, {
      size: 15,
      color: '#aeb4b0',
      weight: 650,
    });
  }
  context.restore();
}

export function adaptMovingTargetDifficulty(
  current: number,
  recentResults: TrialResult[],
): number {
  return adaptDifficultyToPerformance(current, recentResults);
}

export const movingTargetModule: RuntimeTrainingModule<MovingTargetStimulus> = {
  id: 'moving-target',
  label: '動く記号の見分け',
  shortLabel: '移動視標識別',
  protocolVersion: PROTOCOL_VERSION,
  evidenceLabel: '製品化された移動視標課題を参考にした構成',
  instructions: [
    '画面の左・右・上・下のどこから現れ、ランダムに方向を変えて動く、切れ目のある輪を見ます。',
    '輪が消えた後、切れ目の向きを4つの方向ボタンから選びます。',
    '正答状況に合わせて、輪が小さくなり、移動速度が上がり、表示時間も短くなります。',
    'これはアプリ内の移動視標識別であり、眼科検査ではありません。',
  ],
  responseKind: 'single',
  createTrial(seed: number, difficulty: number, mode: SessionMode): TrialPlan<MovingTargetStimulus> {
    const boundedDifficulty = clampDifficulty(difficulty, 1, 5);
    const stimulus = createMovingTargetStimulus(seed, boundedDifficulty);
    return {
      id: `moving-target-${mode}-${seed}`,
      moduleId: 'moving-target',
      protocolVersion: PROTOCOL_VERSION,
      seed,
      stimulusId: `mt-${stimulus.gapDirection}-${stimulus.startSide}-${stimulus.speedDegreesPerSecond}-${stimulus.displayDurationMs}-${stimulus.sizeArcminutes}-${seed}`,
      stimulus,
      correctResponse: stimulus.gapDirection,
      timings: { fixationMs: 700, stimulusMs: stimulus.displayDurationMs, responseWindowMs: 2200 },
      difficulty: boundedDifficulty,
    };
  },
  render: renderMovingTarget,
  isResponseAllowed(response: string): boolean {
    return ['up', 'right', 'down', 'left'].includes(response);
  },
  adaptDifficulty: adaptMovingTargetDifficulty,
  getResponseOptions(): readonly ResponseOption[] {
    return RESPONSE_OPTIONS;
  },
  getFeedback(
    trial: TrialPlan<MovingTargetStimulus>,
    _response: string | undefined,
    correct: boolean,
  ): ModuleFeedback {
    const label = RESPONSE_OPTIONS.find((option) => option.value === trial.correctResponse);
    return {
      title: correct ? '切れ目の向きが一致' : '向きを確認',
      detail: `視標サイズ ${trial.stimulus.sizeArcminutes.toFixed(1)} arcmin ・ ${trial.stimulus.speedDegreesPerSecond.toFixed(1)}°/秒`,
      correctLabel: label?.label ?? 'ー',
    };
  },
  getResultMetadata(trial: TrialPlan<MovingTargetStimulus>) {
    return {
      targetArcminutes: trial.stimulus.sizeArcminutes,
      speedDegreesPerSecond: trial.stimulus.speedDegreesPerSecond,
      displayDurationMs: trial.stimulus.displayDurationMs,
      startSide: trial.stimulus.startSide,
      pathProfile: trial.stimulus.pathProfile,
      pathNodeCount: trial.stimulus.pathNodes.length,
      gapDirection: trial.stimulus.gapDirection,
      referenceProduct: 'DinVA / sports-vision dynamic-acuity systems',
    };
  },
  getThreshold(results: readonly TrialResult[]) {
    const isFixedAssessment = results.some((result) => result.moduleId === 'moving-target' && result.metadata.assessment === true);
    const fixed = estimateFixedFormThreshold(results, {
      moduleId: 'moving-target',
      metadataKey: 'targetArcminutes',
      direction: 'increasing',
    });
    if (fixed !== undefined) return { value: fixed, unit: 'arcmin' };
    if (isFixedAssessment) return undefined;
    const value = estimateReversalThreshold(results, {
      moduleId: 'moving-target',
      metadataKey: 'targetArcminutes',
      lastReversals: 6,
    });
    return value === undefined ? undefined : { value, unit: 'arcmin' };
  },
};
